import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useFetcher } from 'react-router';

import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { useUserLoggedIn } from '@modules/user';
import { AuthModalBrandPane } from '~/modules-rocco/user/component/auth-modal/AuthModalBrandPane';
import { GATE_PANE_BRAND_DEFAULT } from '~/modules-rocco/user/auth-gate/gate-pane-image';
import { useAuthGateSegment } from '~/modules-rocco/user/auth-gate/use-auth-gate-segment';
import { useSocialAuthReturn } from '~/modules-rocco/user/auth-gate/use-social-auth-return';
import { usePublicUser } from '~/modules/user/hook/use-public-user';
import type { DigitalPublicationOption } from '../api/get-digital-publication-options.server';
import type { MemberSubscribeLocation } from '../api/get-member-subscribe-location.server';
import type { SubscribeToPublicationsResult } from '../api/subscribe-to-digital-publications.server';
import { SUBSCRIPTION_PUBLICATIONS_API_ROUTE } from '~/routes/remix-api.subscription.publications';
import { onlyPublicationSeries } from '../config/publication-series';
import { PublicationSelectionPane } from './PublicationSelectionPane';
import { SubscribeForm, type SubscribeFormValues } from './SubscribeForm';
import { SubscribeSuccess } from './SubscribeSuccess';

/** Names this gate on every funnel event and selects its intent copy. */
const SUBSCRIBE_AUTH_SOURCE = 'digitalPublications' as const;

/**
 * Where the reader is among this gate's own steps. `auth` covers everything the
 * auth segment owns — the intent screen, the providers, the account created.
 */
type SubscribeStep = 'select' | 'success';

const EMPTY_FORM: SubscribeFormValues = {
    name: '',
    subscriberType: '',
    locationLabel: '',
    location: null,
    email: '',
};

export interface SubscribePublicationsModalProps {
    open: boolean;
    onClose: () => void;
    /** Carried over from the field that opened this, so it is not typed twice. */
    initialEmail?: string;
    /**
     * The series the reader opened this from — the articles publications row
     * opens it from a single cover. The left pane then shows that one
     * publication, large, rather than the list of four. The gate subscribes
     * to every series regardless.
     */
    initialNewsletterType?: string;
    /**
     * Fired once when the subscription lands, before the account offer. Lets the
     * host refresh anything keyed on subscription status.
     */
    onSubscribed?: () => void;
}

/**
 * Choosing which digital publications to receive, in the revamped two-pane gate.
 *
 * Steps: select → subscribed → (optional) [auth] → account created. The auth
 * half is not this component's: `useAuthGateSegment` owns the providers, the
 * intent copy and the Secure Sign-In offer, exactly as the enquiry and design
 * board gates do, so a change to how people authenticate is made once.
 *
 * The account offer sits after the subscription has already landed, so backing
 * out of it costs the reader nothing they came for.
 */
export const SubscribePublicationsModal = ({
    open,
    onClose,
    initialEmail,
    initialNewsletterType,
    onSubscribed,
}: SubscribePublicationsModalProps) => {
    const userLogin = useUserLoggedIn();
    const [step, setStep] = useState<SubscribeStep>('select');
    const [form, setForm] = useState<SubscribeFormValues>(EMPTY_FORM);

    const optionsFetcher = useFetcher<{
        options: DigitalPublicationOption[];
        memberLocation: MemberSubscribeLocation | null;
    }>();
    const subscribeFetcher = useFetcher<SubscribeToPublicationsResult>();

    // Memoised because the `?? []` fallback is a fresh array on every render,
    // which would re-run the seeding effect below forever.
    const memberLocation = optionsFetcher.data?.memberLocation ?? null;
    // Only the four series, in the pane's order: the catalogue answers with
    // every subscription type HubSpot holds, and the rest are not on offer.
    const publications = useMemo(
        () => onlyPublicationSeries(optionsFetcher.data?.options ?? []),
        [optionsFetcher.data],
    );
    /** No answer yet — not "there are none", which is what an empty pane says. */
    const loadingOptions = optionsFetcher.data === undefined;
    const submitting = subscribeFetcher.state !== 'idle';
    const result = subscribeFetcher.data;

    /**
     * The far side of a social round-trip this gate started. Single use — the
     * modal outlives the journey it describes.
     */
    const { spend: spendSocialAuth } = useSocialAuthReturn(
        SUBSCRIBE_AUTH_SOURCE,
    );

    /**
     * Account detection for the address that was subscribed. A reader whose email
     * already has an account must be asked to log in, not pitched a second one —
     * and the segment needs the resolved user to skip its own email step.
     */
    const { publicUser, handleUserExists, publicUserLoginFlow } = usePublicUser(
        SUBSCRIBE_AUTH_SOURCE,
    );

    const tracker = useTracker();
    // The publications funnel is the articles directory's only revenue
    // conversion and reported nothing at all, so opens, submits and completions
    // were equally invisible — there was no denominator for any of them.
    const openReportedRef = useRef(false);
    useEffect(() => {
        if (!open || openReportedRef.current) return;
        openReportedRef.current = true;
        void tracker.log('SubscribeModalOpened', {
            url: new URL(window.location.href),
            data: {
                Source: SUBSCRIBE_AUTH_SOURCE,
                ExtraData: JSON.stringify({
                    newsletterType: initialNewsletterType ?? null,
                }),
            },
        });
    }, [open, initialNewsletterType, tracker]);

    const gateRef = useRef<ReturnType<typeof useAuthGateSegment> | null>(null);
    const hasOpenedRef = useRef(false);

    // On mount, not on open. This component is only mounted once the trigger is
    // hovered or focused, so being here already means the modal is wanted — and
    // fetching from that moment rather than the click is what puts the
    // publications on screen instead of a loading pane.
    useEffect(() => {
        if (optionsFetcher.state !== 'idle' || optionsFetcher.data) return;
        optionsFetcher.load(SUBSCRIPTION_PUBLICATIONS_API_ROUTE);
    }, [optionsFetcher]);

    // Prefill from the field that opened this, then from the signed-in member —
    // in that order, because an address just typed is the more deliberate of the
    // two. Only onto an untouched form, so a reader who has started typing does
    // not have it replaced under them.
    const member = userLogin.isLoggedIn ? userLogin.user : null;
    useEffect(() => {
        if (!open) return;
        setForm((current) => {
            if (
                current.email !== '' ||
                current.name !== '' ||
                current.locationLabel !== ''
            ) {
                return current;
            }
            const email = initialEmail?.trim() || member?.Email || '';
            const name = member
                ? [member.FirstName, member.LastName]
                      .filter(Boolean)
                      .join(' ')
                      .trim()
                : '';
            // The saved location comes from the catalogue loader, which resolves
            // the member's region code into the name HubSpot segments on.
            const locationLabel = memberLocation?.label ?? '';
            const location = memberLocation?.region
                ? { Region: memberLocation.region }
                : null;

            return email === '' && name === '' && locationLabel === ''
                ? current
                : { ...current, email, name, locationLabel, location };
        });
    }, [open, member, initialEmail, memberLocation]);

    // Notified once, on the transition into success, so a host keyed on
    // subscription status refreshes without re-firing on every later render.
    const notifiedRef = useRef(false);
    const handleSubscribed = useCallback(() => {
        setStep('success');
        if (notifiedRef.current) return;
        notifiedRef.current = true;
        void tracker.log('SubscribeCompleted', {
            url: new URL(window.location.href),
            data: {
                Source: SUBSCRIBE_AUTH_SOURCE,
                ExtraData: JSON.stringify({
                    publications: publications.length,
                }),
            },
        });
        onSubscribed?.();
    }, [onSubscribed, publications.length, tracker]);

    // The subscription landed — move to the confirmation. Watched rather than
    // awaited so a social round-trip that reloads mid-flight cannot strand it.
    useEffect(() => {
        if (subscribeFetcher.state !== 'idle' || !result?.success) return;
        handleSubscribed();
    }, [subscribeFetcher.state, result, handleSubscribed]);

    const gate = useAuthGateSegment({
        authSource: SUBSCRIBE_AUTH_SOURCE,
        // The whole segment is behind them, account and all. There is nothing
        // left to resume — the subscription landed before the offer was made —
        // so this is simply the end of the journey.
        onResolved: () => onClose(),
    });
    gateRef.current = gate;

    const handleSubmit = useCallback(() => {
        // Detection runs alongside the subscribe, not after it. Started on the
        // success screen instead, the offer renders as "Create an account" and
        // then flips to "Log in" a moment later, in front of the reader. The
        // subscribe round trip is cover for it. A signed-in reader is never shown
        // the offer, so there is nothing to detect for them.
        if (!userLogin.isLoggedIn && form.email) {
            void handleUserExists(form.email);
        }

        const data = new FormData();
        data.set('email', form.email);
        data.set('name', form.name);
        data.set('subscriberType', form.subscriberType);
        // Only a picked place's region is sent. A half-typed place is refused
        // by the server rather than guessed at.
        data.set('region', form.location?.Region ?? '');
        // HubSpot files the submission against the page it came from.
        data.set('pageUri', window.location.href);
        data.set('pageName', document.title);

        void tracker.log('SubscribeSubmitted', {
            url: new URL(window.location.href),
            data: {
                Source: SUBSCRIBE_AUTH_SOURCE,
                ExtraData: JSON.stringify({
                    publications: publications.length,
                    loggedIn: userLogin.isLoggedIn,
                }),
            },
        });
        subscribeFetcher.submit(data, {
            method: 'post',
            action: SUBSCRIPTION_PUBLICATIONS_API_ROUTE,
        });
    }, [
        form,
        publications.length,
        subscribeFetcher,
        userLogin.isLoggedIn,
        handleUserExists,
        tracker,
    ]);

    const isReturning = publicUser?.type === 'existing';

    const handleAccountStep = useCallback(() => {
        gateRef.current?.open({
            // Passing the resolved user skips the segment's own email step, so a
            // reader who just typed their address is not asked for it again.
            publicUser,
            flow: publicUserLoginFlow ?? 'sign-up',
            message: isReturning
                ? 'Log in to your ArchiPro account'
                : 'Create your free ArchiPro account',
        });
    }, [publicUser, publicUserLoginFlow, isReturning]);

    const handleClose = useCallback(() => {
        gateRef.current?.close();
        onClose();
    }, [onClose]);

    // A fresh open starts on the selection again; the previous journey is over.
    useEffect(() => {
        if (open) {
            hasOpenedRef.current = true;
            return;
        }
        setStep('select');
        setForm(EMPTY_FORM);
        notifiedRef.current = false;
        if (hasOpenedRef.current) {
            hasOpenedRef.current = false;
            spendSocialAuth();
        }
        gateRef.current?.reset();
    }, [open, spendSocialAuth]);

    const showGate = gate.isActive;
    const showSuccess = !showGate && step === 'success';

    // The selection stays beside the form while it is being made; past that the
    // pane becomes brand imagery, matching the success and auth frames.
    //
    // The success frame carries no overlaid statement — the confirmation and the
    // account pitch are both on the right, and a third message competing with them
    // is what the design leaves out. The auth steps keep the standardised pane and
    // its intent copy, which the segment renders itself.
    const pane =
        showGate || showSuccess ? (
            <AuthModalBrandPane statement="" image={GATE_PANE_BRAND_DEFAULT} />
        ) : (
            <PublicationSelectionPane
                publications={publications}
                loading={loadingOptions}
                featuredNewsletterType={initialNewsletterType}
            />
        );

    return (
        <TwoPaneModal
            corners="rounded"
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) handleClose();
            }}
            title={
                showGate
                    ? 'Create an ArchiPro account'
                    : showSuccess
                      ? 'Subscribed'
                      : 'Subscribe to ArchiPro Digital Publications'
            }
            bodyPadding="roomy"
            pane={pane}
        >
            {showGate ? (
                gate.element
            ) : showSuccess ? (
                <SubscribeSuccess
                    account={
                        // A reader with a session already has the profile this
                        // pitches, so there is nothing to offer them.
                        userLogin.isLoggedIn
                            ? null
                            : isReturning
                              ? {
                                    label: 'Log in to ArchiPro',
                                    heading: 'You already have an account',
                                    body: 'Log in to manage what you receive, save projects, create Design Boards and message professionals.',
                                    onSelect: handleAccountStep,
                                }
                              : {
                                    label: 'Create an ArchiPro Account',
                                    heading: 'Building or renovating?',
                                    body: 'A free ArchiPro profile lets you save projects, create Design Boards, manage Product Schedules, message Pro’s, and Get Matched with the right people for your build.',
                                    onSelect: handleAccountStep,
                                }
                    }
                    onDismiss={handleClose}
                />
            ) : (
                <SubscribeForm
                    values={form}
                    onChange={setForm}
                    onSubmit={handleSubmit}
                    onCancel={handleClose}
                    submitting={submitting}
                    error={
                        result && !result.success
                            ? { message: result.message, field: result.field }
                            : undefined
                    }
                    // The side pane is desktop-only, so the sheet carries the
                    // publications inline rather than losing them.
                    selectionSlot={
                        <div className="mt-5 md:hidden">
                            <PublicationSelectionPane
                                publications={publications}
                                compact
                                loading={loadingOptions}
                            />
                        </div>
                    }
                />
            )}
        </TwoPaneModal>
    );
};
